import { useRef, useEffect, useState, useCallback } from 'react';
import MessageBubble from './MessageBubble';

function groupMessagesByDay(messages) {
  const groups = [];
  let currentDate = null;
  let currentGroup = [];
  for (const msg of messages) {
    const d = msg.createdAt ? new Date(msg.createdAt).toDateString() : '';
    if (d !== currentDate) {
      if (currentGroup.length) groups.push({ date: currentDate, messages: currentGroup });
      currentDate = d;
      currentGroup = [];
    }
    currentGroup.push(msg);
  }
  if (currentGroup.length) groups.push({ date: currentDate, messages: currentGroup });
  return groups;
}

function dayLabel(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Danas';
  if (date.toDateString() === yesterday.toDateString()) return 'Juče';
  return date.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function MessageList({
  messages,
  currentUserId,
  senderDisplayName,
  loading,
  onCopy,
  onReply,
  onDelete,
  onEdit,
  scrollToMessageId,
  t = (s) => s,
}) {
  const containerRef = useRef(null);
  const bottomRef = useRef(null);
  const messageRefs = useRef({});
  const [isNearBottom, setIsNearBottom] = useState(true);
  const prevLengthRef = useRef(0);

  const checkNearBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    setIsNearBottom(near);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || messages.length === 0) return;
    if (prevLengthRef.current < messages.length && isNearBottom) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
    prevLengthRef.current = messages.length;
  }, [messages.length, isNearBottom]);

  useEffect(() => {
    if (!scrollToMessageId) return;
    const el = messageRefs.current[scrollToMessageId] || containerRef.current?.querySelector(`[data-message-id="${scrollToMessageId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [scrollToMessageId]);

  const hasNewMessagesBelow = !isNearBottom && messages.length > 0;

  const listClassName = 'flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y';

  if (loading) {
    return (
      <div className={`${listClassName} p-4 space-y-4`}>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
            <div className="w-3/4 max-w-xs h-12 rounded-2xl bg-gray-200 dark:bg-gray-700 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className={`${listClassName} flex flex-col items-center justify-center p-6 text-center`}>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Nema poruka. Pošaljite prvu!
        </p>
      </div>
    );
  }

  const groups = groupMessagesByDay(messages);

  return (
    <div
      ref={containerRef}
      onScroll={checkNearBottom}
      className={`${listClassName} p-4 space-y-4`}
      role="log"
      aria-live="polite"
    >
      {groups.map((group) => (
        <div key={group.date}>
          <div className="flex justify-center my-3">
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
              {dayLabel(group.date)}
            </span>
          </div>
          <div className="space-y-2">
            {group.messages.map((msg) => (
              <div key={msg.id} data-message-id={msg.id} ref={(r) => { if (r) messageRefs.current[msg.id] = r; }}>
                <MessageBubble
                  message={msg}
                  isOwn={msg.senderId === currentUserId}
                  isSent={msg.senderId === currentUserId && msg.id && !String(msg.id).startsWith('temp-')}
                  senderDisplayName={senderDisplayName}
                onCopy={onCopy}
                onReply={onReply}
                onDelete={onDelete}
                onEdit={onEdit}
                canEdit={msg.senderId === currentUserId}
                t={t}
              />
              </div>
            ))}
          </div>
        </div>
      ))}
      {hasNewMessagesBelow && (
        <div className="flex justify-center sticky bottom-2">
          <button
            type="button"
            onClick={() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); setIsNearBottom(true); }}
            className="px-4 py-2 rounded-full bg-primary-500 text-white text-sm font-medium shadow-lg hover:bg-primary-600"
          >
            Nove poruke
          </button>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
